import {useTranslation} from 'react-i18next';
import {useMemo, useState, useCallback} from 'react';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import useScreenSize from '../../../../../hooks/useScreenSize';
import AppButton from '../../../../../components/common/AppButton';
import AppTextInput from '../../../../../components/common/AppTextInput';
import AppConfirmationDialog from '../../../../../components/common/AppConfirmationDialog';
import {SVG} from '../../../../../constants/assetLists';
import {useChecklistContext} from '../../../../../context/checklist/ChecklistContextProvider';
import {useClientsContext} from '../../../../../context/clients/ClientsContextProvider';
import QUERY_KEYS from '../../../../../constants/queryKeys';
import {handleBulkArchiveChecklistService} from '../../../../../services/operations/checklist';
import {useSnackbar} from '../../../../../context/SnackbarContextProvider';
import AddChecklists from './AddChecklists';
import ChecklistMenuSection from './ChecklistMenuSection';
import {
  getChecklistLocationStatus,
  getChecklistLocationType,
} from '../../../checklistLocationUtils';

function ChecklistHeader() {
  /** ---------------- Language & Layout ---------------- */
  const {i18n} = useTranslation();
  const isHebrew = useMemo(() => i18n.language === 'heb', [i18n.language]);

  const screenSize = useScreenSize();
  const isLarge = screenSize === 'xl' || screenSize === '2xl';

  /** ---------------- External Hooks ---------------- */
  const {showSnackbar} = useSnackbar();
  const queryClient = useQueryClient();

  const {
    flatChecklists,
    setSelectedChecklist,
    selectedChecklists,
    setSelectedChecklists,
    searchText,
    setSearchText,
    setShowAddEditDialog,
    setCopyMode,
  } = useChecklistContext();

  const {selectedLocation} = useClientsContext();

  /** ---------------- Local State ---------------- */
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  /** ---------------- Derived Values ---------------- */
  const isOneSelected = selectedChecklists.length === 1;
  const disableDelete = !(selectedChecklists.length >= 1);

  const totalChecklists = flatChecklists.length;

  // Check if any selected checklist has 'cancelled' status (branch/sub-branch only)
  const hasSelectedCancelledChecklist = useMemo(() => {
    if (
      !selectedChecklists.length ||
      !flatChecklists.length ||
      !selectedLocation ||
      selectedLocation.isClient
    ) {
      return false;
    }

    return selectedChecklists.some(checklistId => {
      const checklist = flatChecklists.find(
        (c: any) => c._id === checklistId,
      );
      if (!checklist) return false;

      return getChecklistLocationStatus(checklist, selectedLocation) === 'cancelled';
    });
  }, [selectedChecklists, flatChecklists, selectedLocation]);

  const isSelectedDraftOnBranch = useMemo(() => {
    if (
      !isOneSelected ||
      !flatChecklists.length ||
      !selectedLocation ||
      selectedLocation.isClient
    ) {
      return false;
    }
    const checklist = flatChecklists.find(
      (c: any) => c._id === selectedChecklists[0],
    );
    return !!checklist?.isDraft;
  }, [
    isOneSelected,
    selectedChecklists,
    flatChecklists,
    selectedLocation,
  ]);

  // Check if any selected checklist has 'inactive' status at the current location
  const hasSelectedInactiveChecklist = useMemo(() => {
    if (
      !selectedChecklists.length ||
      !flatChecklists.length ||
      !selectedLocation
    ) {
      return false;
    }

    return selectedChecklists.some(checklistId => {
      const checklist = flatChecklists.find(
        (c: any) => c._id === checklistId,
      );
      if (!checklist) return false;

      return getChecklistLocationStatus(checklist, selectedLocation) === 'inactive';
    });
  }, [selectedChecklists, flatChecklists, selectedLocation]);

  /** ---------------- Mutations ---------------- */
  const {mutate: handleBulkArchiveChecklist} = useMutation({
    mutationFn: (payload: {
      checklistIds: string[];
      status: 'inactive';
      locationId?: string;
      locationType?: 'client' | 'branch' | 'subBranch';
    }) => handleBulkArchiveChecklistService(payload),

    onSuccess: data => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_ALL_CHECKLISTS],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_GROUPED_CHECKLISTS],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_ALL_CHECKLISTS_SHIFT_ID],
      });

      showSnackbar({
        message: data?.message ?? 'רשימת התיוג הועברה לארכיון בהצלחה',
        type: 'success',
      });
    },
  });

  /** ---------------- Handlers ---------------- */
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchText(e.target.value);
    },
    [setSearchText],
  );

  const clearSearch = useCallback(() => {
    setSearchText('');
  }, [setSearchText]);

  const handleDeleteConfirm = useCallback(() => {
    handleBulkArchiveChecklist({
      checklistIds: selectedChecklists as string[],
      status: 'inactive',
      // Pass location context for per-location status updates
      ...(selectedLocation && {
        locationId: selectedLocation.id,
        locationType: getChecklistLocationType(selectedLocation),
      }),
    });

    setSelectedChecklists([]);
    setShowDeleteConfirmation(false);
  }, [
    selectedChecklists,
    handleBulkArchiveChecklist,
    setSelectedChecklists,
    selectedLocation,
  ]);

  const openAddEditChecklist = () => {
    setSelectedChecklist(selectedChecklists[0]);
    setSelectedChecklists([]);

    setShowAddEditDialog(true);
  };

  const openCopyChecklist = () => {
    setSelectedChecklist(selectedChecklists[0]);
    setSelectedChecklists([]);
    setCopyMode(true);
    setShowAddEditDialog(true);
  };

  /** ---------------- UI ---------------- */
  return (
    <>
      <div
        className={`w-full py-4 bg-white px-4 flex flex-wrap items-center justify-between gap-3 shadow-md z-2
          ${isLarge ? (isHebrew ? 'rounded-tl-[32px]' : 'rounded-tr-[32px]') : ''}
        `}>
        <div className="flex-1 flex flex-wrap items-center gap-3">
          {/* Left actions */}
          <div className="flex items-center gap-2">
            <AddChecklists />

            <AppButton
              variant="primary"
              py="12px"
              disabled={
                !isOneSelected ||
                hasSelectedCancelledChecklist ||
                isSelectedDraftOnBranch
              }
              iconStart={
                <img
                  src={SVG.editPencilWhiteIcon}
                  className="w-[14px] h-[14px]"
                />
              }
              onClick={() => openAddEditChecklist()}>
              עריכה
            </AppButton>

            <AppButton
              variant="primary"
              py="12px"
              disabled={!isOneSelected}
              iconStart={
                <img src={SVG.copyWhiteIcon} className="w-[18px] h-[18px]" />
              }
              onClick={() => openCopyChecklist()}>
              העתק
            </AppButton>

            <AppButton
              variant="primary"
              py="12px"
              disabled={
                disableDelete ||
                hasSelectedCancelledChecklist ||
                hasSelectedInactiveChecklist
              }
              iconStart={
                <img src={SVG.whiteCloseIcon} className="w-[14px] h-[14px]" />
              }
              onClick={() => setShowDeleteConfirmation(true)}>
              ארכיון
            </AppButton>
          </div>

          {/* Search */}
          <div className="flex items-center gap-3">
            <AppTextInput
              placeholder="חיפוש צ'קליסט"
              value={searchText}
              onChange={handleSearchChange}
              containerClassName="!w-[499px]"
              className="bg-transparent !rounded-[28px] pl-16"
              endIcon={
                <div className="flex items-center gap-1">
                  {searchText && (
                    <button
                      type="button"
                      onClick={clearSearch}
                      className="w-[24px] h-[24px] flex justify-center items-center rounded-full hover:bg-gray20">
                      <img
                        src={SVG.closeDarkIcon}
                        className="w-[12px] h-[12px]"
                        alt="clear"
                      />
                    </button>
                  )}
                  <div
                    className={`w-[24px] h-[24px] flex justify-center items-center rounded-full ${
                      searchText ? 'bg-aqua' : ''
                    }`}>
                    <img src={SVG.searchIcon} className="w-[18px] h-[18px]" />
                  </div>
                </div>
              }
            />

            <ChecklistMenuSection />
          </div>
        </div>

        <div>
          <p className="m-0 text-black">{totalChecklists} צ’קליסטים</p>
        </div>
      </div>

      <AppConfirmationDialog
        open={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={handleDeleteConfirm}
        icon={<img src={SVG.deleteRedIcon} className="w-[48px] h-[48px]" />}
        title="שימו לב"
        subTitle="הצ'קליסט יעבור לארכיון, האם להמשיך?"
        confirmBtnText="כן"
        cancelBtnText="לא"
      />
    </>
  );
}

export default ChecklistHeader;
